import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

@Injectable({ providedIn: 'root' })
export class ToasterService {
  private idCounter = 0;

  // 🔔 Internal signal for toasts
  private _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  /**
   * Generic toast method
   * @param message - The toast message
   * @param type - Type of the toast
   * @param duration - Duration in ms before auto-dismiss
   */
  show(message: string, type: Toast['type'] = 'info', duration = 3000): void {
    const id = ++this.idCounter;
    const newToast: Toast = { id, message, type };

    this._toasts.update((toasts) => [...toasts, newToast]);

    setTimeout(() => this.remove(id), duration);
  }

  /** Show success toast */
  showSuccess(message: string, duration = 3000): void {
    this.show(message, 'success', duration);
  }

  /** Show error toast */
  showError(message: string, duration = 3000): void {
    this.show(message, 'error', duration);
  }

  /** Show info toast */
  showInfo(message: string, duration = 3000): void {
    this.show(message, 'info', duration);
  }

  /** Show warning toast */
  showWarning(message: string, duration = 3000): void {
    this.show(message, 'warning', duration);
  }

  /** Removes a toast by its ID */
  private remove(id: number): void {
    this._toasts.update((toasts) => toasts.filter((t) => t.id !== id));
  }
}
